import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cx } from '../../lib/cx.ts';
import { focusRing } from '../../lib/focus.ts';
import { Spinner } from '../spinner/spinner.tsx';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'bar' | 'touch';

const BASE = cx(
  'inline-flex shrink-0 cursor-pointer items-center justify-center gap-1.5 font-sans font-medium',
  'whitespace-nowrap select-none disabled:cursor-not-allowed disabled:not-aria-busy:opacity-50',
  // A loading button is busy, not unavailable: its label and spinner keep full contrast.
  'aria-busy:cursor-progress',
  // Colours ease on hover; a press sets the button down by a pixel.
  'motion-safe:transition-[color,background-color,border-color,box-shadow,filter,translate] enabled:active:translate-y-px',
  focusRing,
);

/**
 * Heights from the mocks: 28 (Backlog sprint header), 30 (board drawer and Issue toolbar),
 * 32 (page headers; the control height token), 38 (Setup wizard) and the top bar's Create,
 * which is sized by its padding (6px 14px, 29px tall).
 */
const SIZES: Record<ButtonSize, string> = {
  xs: 'h-7 rounded-chip px-2.5 text-13',
  sm: 'h-7.5 rounded-chip px-2.5 text-13',
  md: 'h-control rounded-control text-13',
  lg: 'h-9.5 rounded-control text-14',
  bar: 'rounded-control py-1.5 text-13',
  /** The sign-in pages: 40px, and 46px with 16px text on phones. */
  touch: 'h-10 rounded-control text-14 max-sm:h-11.5 max-sm:text-16',
};

/** Primary buttons carry more padding than bordered ones at the same height, as in the mocks. */
const PADDING: Partial<Record<`${ButtonVariant}-${ButtonSize}`, string>> = {
  'primary-md': 'px-3.5',
  'danger-md': 'px-3.5',
  'secondary-md': 'px-3',
  'ghost-md': 'px-3',
  'primary-lg': 'px-4.5',
  'danger-lg': 'px-4.5',
  'secondary-lg': 'px-3.5',
  'ghost-lg': 'px-3.5',
  'primary-bar': 'px-3.5',
  'secondary-bar': 'px-3.5',
  'ghost-bar': 'px-3.5',
  'danger-bar': 'px-3.5',
  'primary-touch': 'px-4',
  'secondary-touch': 'px-3.5',
  'ghost-touch': 'px-3.5',
  'danger-touch': 'px-4',
};

const VARIANTS: Record<ButtonVariant, string> = {
  // A key hint inside a filled button takes the button's ink; the Kbd grey reads at 1.3:1 there.
  primary: cx(
    'border-0 bg-acc-fill text-on-acc enabled:hover:brightness-95 enabled:active:brightness-90',
    '[&_kbd]:border-on-acc/30 [&_kbd]:bg-transparent [&_kbd]:text-on-acc',
  ),
  // The hover and press overlays tint whatever the button sits on, the same on every surface.
  secondary: cx(
    'border border-line bg-card text-tx-2 enabled:hover:text-tx',
    'enabled:hover:shadow-[inset_0_0_0_99px_var(--hover)] enabled:active:shadow-[inset_0_0_0_99px_var(--press)]',
  ),
  ghost:
    'border-0 bg-transparent text-tx-2 enabled:hover:bg-hover enabled:hover:text-tx enabled:active:bg-press',
  danger: cx(
    'border-0 bg-red text-on-solid enabled:hover:brightness-95 enabled:active:brightness-90',
    '[&_kbd]:border-on-solid/30 [&_kbd]:bg-transparent [&_kbd]:text-on-solid',
  ),
};

export interface ButtonStyle {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  /** 10px side padding, as on the filter and menu triggers ("Epic ▾"). */
  tight?: boolean;
  className?: string;
}

/** The classes of a button, for links and other elements that must look like one. */
export function buttonClassName({
  variant = 'secondary',
  size = 'md',
  block,
  tight,
  className,
}: ButtonStyle = {}): string {
  const padding = tight ? 'px-2.5' : PADDING[`${variant}-${size}`];
  return cx(BASE, SIZES[size], padding, VARIANTS[variant], block && 'w-full', className);
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, ButtonStyle {
  /** Leading icon, replaced by a spinner while loading. */
  icon?: ReactNode;
  /** Trailing content such as a caret or a key hint. */
  iconEnd?: ReactNode;
  /** Disables the button and shows a spinner; the label stays so the width does not jump. */
  loading?: boolean;
}

export function Button({
  variant,
  size,
  block,
  tight,
  className,
  icon,
  iconEnd,
  loading = false,
  disabled,
  type = 'button',
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={buttonClassName({ variant, size, block, tight, className })}
      {...rest}
    >
      {loading ? <Spinner /> : icon}
      {children}
      {iconEnd}
    </button>
  );
}
